import { useAuthState } from '@auth/hooks';
import { useCommunities } from '@communities/community/hooks';
import { useCommunityGroupDetail } from '@communities/community-groups/hooks';
import { UserRoles } from '@core/domain';
import { Icon, IconSize, ChevronRight, SlideSheet } from '@soteria/design-system-ve';
import { useNavigate } from 'react-router-dom';

import { config } from '../../../config';

import { CommunityList } from './CommunityList';
import { NavigationButtons } from './NavigationButtons';
import * as styles from './NavigationSlideout.css';

interface NavigationSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NavigationSlideout({ isOpen, onClose }: NavigationSlideoutProps) {
  // Get communities for the selected group
  const { data: communities = [] } = useCommunities();

  // Get current community group
  const { data: communityGroup } = useCommunityGroupDetail();

  // Get current user for admin check
  const { isUserSystemAdmin, hasRoleAccess } = useAuthState();

  // Check if user has admin access (system admin or community group admin)
  const hasAdminAccess = isUserSystemAdmin || Boolean(communityGroup?.id && hasRoleAccess(UserRoles.ADMIN, communityGroup.id));

  // Navigation hook
  const navigate = useNavigate();

  // Handle Synesis Data Management navigation (external link)
  const handleSynesisClick = () => {
    const synesisUrl = config.externalServices.synesisUrl;

    if (synesisUrl) {
      // Open in new tab to maintain context in Soteria
      window.open(synesisUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle system configuration navigation
  const handleSystemConfigClick = () => {
    navigate('/label-management');
    onClose();
  };

  // Handle community navigation
  const handleCommunityClick = (communityId: string) => {
    navigate(`/communities/${communityId}`);
    onClose();
  };

  // Handle menu item clicks
  const handleMenuItemClick = (path: string) => {
    navigate(path);
    onClose();
  };

  // Handle community group home navigation
  const handleCommunityGroupClick = () => {
    if (communityGroup?.id) {
      navigate(`/community-groups/${communityGroup.id}`);
      onClose();
    }
  };

  // Handle all community groups navigation
  const handleCommunityGroupsClick = () => {
    navigate('/community-groups');
    onClose();
  };

  return (
    <SlideSheet
      darkHeader
      showBackdrop
      className={styles.navigationSlideout}
      contentClassName={styles.noPaddingContent}
      isOpen={isOpen}
      side="left"
      size="md"
      title="Soteria"
      onClose={onClose}
    >
      {/* Main content container with proper flexbox layout */}
      <div className={styles.slideoutContent}>
        {/* Navigation Buttons Section */}
        <div className={communityGroup ? styles.homeButtonSection : styles.communityGroupHeader}>
          <NavigationButtons
            hasAdminAccess={hasAdminAccess}
            isUserSystemAdmin={isUserSystemAdmin}
            onCommunityGroupsClick={handleCommunityGroupsClick}
            onSynesisClick={handleSynesisClick}
            onSystemConfigClick={handleSystemConfigClick}
          />
        </div>

        {/* Selected Community Group Section Header */}
        {communityGroup && (
          <div className={styles.communityGroupHeader}>
            <button className={styles.homeButton} onClick={handleCommunityGroupClick}>
              <h2>{communityGroup.name}</h2>
              <Icon className={styles.chevronIcon} icon={ChevronRight} iconSize={IconSize.XSmall} />
            </button>
            <h3 className={styles.communitiesSectionTitle}>Communities</h3>
          </div>
        )}

        {/* Communities list */}
        {communityGroup ? (
          <div className={styles.communitiesSection}>
            <CommunityList
              communities={communities}
              hasRoleAccess={hasRoleAccess}
              onCommunityClick={handleCommunityClick}
              onMenuItemClick={handleMenuItemClick}
            />
          </div>
        ) : (
          <div className={styles.communitiesSection} />
        )}
      </div>
    </SlideSheet>
  );
}
